import { useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiClientError } from '../api/client';
import { listAllCompanies, listAllEmployees } from '../api/directory';
import {
  checkRoomCalendar,
  createRoom,
  deleteRoom,
  listRooms,
  roomImpact,
  updateRoom,
  type Room,
  type RoomStatus,
} from '../api/rooms';
import { ClickableRow, RowTitle } from '../components/ClickableRow';
import { Modal } from '../components/Modal';
import { PeoplePicker } from '../components/PeoplePicker';
import { useToast } from '../components/Toasts';
import { Icon } from '../lib/icons';
import { ROOM_BADGE, badgeFor } from '../lib/statusStyles';

const STATUSES: RoomStatus[] = ['available', 'maintenance', 'closed'];

/**
 * FEAT-007 — Meeting Rooms, under Administration.
 *
 * Laid out as the prototype has it: a `page-head` with the eyebrow and lead, the
 * explanatory `note`, then the table of rooms with capacity, access chips and a
 * status badge.
 */
export function RoomsPage(): ReactElement {
  const { t } = useTranslation();
  const { notify } = useToast();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<Room | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deleting, setDeleting] = useState<Room | null>(null);
  const [checking, setChecking] = useState<string | null>(null);

  const rooms = useQuery({ queryKey: ['rooms'], queryFn: listRooms });

  /**
   * FEAT-018 Stage 5 — read the room's resource calendar and say what happened.
   *
   * Not a mutation and not cached: it is a live question about a connection that
   * an administrator is in the middle of fixing, so a cached "it worked ten
   * minutes ago" would be the one answer that helps least.
   *
   * The result is a toast rather than a panel because it is a moment, not a
   * state — and because failing loudly in the same place every other action
   * reports is what makes it get read.
   */
  async function runCheck(room: Room): Promise<void> {
    setChecking(room.id);

    try {
      const result = await checkRoomCalendar(room.id);

      if (result.ok) {
        notify(
          t('rm_check_ok', {
            room: room.name,
            count: result.events_found ?? 0,
            ours: result.events_from_boud ?? 0,
          }),
          'ok',
        );
        return;
      }

      /*
       * The failure code is localised here; `detail` is the provider's own
       * words, appended untranslated because it is diagnostic text an
       * administrator will paste into a support thread.
       */
      const message = t(`rm_check_${result.failure ?? 'unreachable'}`, { room: room.name });
      notify(result.detail ? `${message} — ${result.detail}` : message, 'bad');
    } catch (error) {
      notify(error instanceof ApiClientError ? error.message : t('login_network_error'), 'bad');
    } finally {
      setChecking(null);
    }
  }

  /*
   * The allow-lists hold ids; the table shows names. Fetched once here and
   * shared, so every row reads from the same resolved map.
   */
  const companies = useQuery({
    queryKey: ['companies', 'all'],
    queryFn: listAllCompanies,
  });

  const people = useQuery({
    queryKey: ['employees', 'all'],
    queryFn: listAllEmployees,
  });

  const companyNames = useMemo(
    () => new Map((companies.data ?? []).map((c) => [c.id, c.name])),
    [companies.data],
  );

  const personNames = useMemo(
    () => new Map((people.data ?? []).map((p) => [p.id, p.name])),
    [people.data],
  );

  function refresh(): void {
    void queryClient.invalidateQueries({ queryKey: ['rooms'] });
    // The calendar's room columns come from the same catalogue.
    void queryClient.invalidateQueries({ queryKey: ['bookable-rooms'] });
    void queryClient.invalidateQueries({ queryKey: ['bookings'] });
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">{t('nav_admin')}</div>
          <h1>{t('rooms')}</h1>
          <div className="lead">{t('rooms_lead')}</div>
        </div>
        <button type="button" className="btn primary" onClick={() => setIsCreating(true)}>
          <Icon name="plus" />
          {t('rm_new')}
        </button>
      </div>

      <div className="note">
        <Icon name="info" />
        <div>{t('rooms_note')}</div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>{t('rooms')}</h2>
          <span className="hint">{rooms.data?.length ?? 0}</span>
        </div>

        {rooms.isPending ? (
          <div className="empty" role="status" aria-live="polite">
            {t('loading')}
          </div>
        ) : rooms.error ? (
          <div className="empty" role="alert">
            {rooms.error instanceof ApiClientError ? rooms.error.message : t('login_network_error')}
          </div>
        ) : rooms.data && rooms.data.length === 0 ? (
          <div className="empty">
            <div className="em-ic">
              <Icon name="room" />
            </div>
            {t('none_yet')}
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">{t('rm_name')}</th>
                  <th scope="col">{t('bk_cap')}</th>
                  <th scope="col">{t('rm_access')}</th>
                  <th scope="col">{t('rm_status')}</th>
                  <th scope="col">
                    <span className="sr-only">{t('actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rooms.data?.map((room) => (
                  <ClickableRow key={room.id} onOpen={() => setEditing(room)}>
                    <td>
                      <RowTitle onOpen={() => setEditing(room)}>{room.name}</RowTitle>
                    </td>
                    <td className="num">{room.capacity}</td>
                    <td>
                      <div className="acc-cell">
                        <AccessChips
                          room={room}
                          companyNames={companyNames}
                          personNames={personNames}
                        />
                      </div>
                    </td>
                    <td>
                      <span className={badgeFor(ROOM_BADGE, room.status)}>
                        {t(`rs_${room.status}`)}
                      </span>
                    </td>
                    <td>
                      <div className="row-actions">
                        {/*
                          FEAT-018 Stage 5 — offered only for a room that has a
                          resource calendar. On one that has none the answer is
                          always the same, and a control whose result you can
                          predict is a control that teaches people to ignore it.
                        */}
                        {room.resource_email ? (
                          <button
                            type="button"
                            className="btn sm"
                            disabled={checking === room.id}
                            onClick={() => void runCheck(room)}
                            aria-label={`${t('rm_check')} — ${room.name}`}
                          >
                            <Icon name={checking === room.id ? 'clock' : 'refresh'} />
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="btn sm"
                          onClick={() => setEditing(room)}
                          aria-label={`${t('rm_edit')} — ${room.name}`}
                        >
                          <Icon name="edit" />
                        </button>
                        <button
                          type="button"
                          className="btn sm danger"
                          onClick={() => setDeleting(room)}
                          aria-label={`${t('rm_del')} — ${room.name}`}
                        >
                          <Icon name="trash" />
                        </button>
                      </div>
                    </td>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isCreating || editing ? (
        <RoomDialog
          room={editing}
          onClose={() => {
            setIsCreating(false);
            setEditing(null);
          }}
          onSaved={(created) => {
            setIsCreating(false);
            setEditing(null);
            refresh();
            notify(created ? t('rm_added') : t('rm_saved'));
          }}
        />
      ) : null}

      {deleting ? (
        <DeleteRoomDialog
          room={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            refresh();
            notify(t('rm_deleted'));
          }}
        />
      ) : null}
    </>
  );
}

/**
 * BR-019 rendered as the prototype renders it: one chip reading "Everyone" when
 * both lists are empty, otherwise a chip per grantee.
 *
 * The names are resolved from lookups the page already holds, rather than by
 * each cell fetching for itself. Per-cell queries made the rendered text depend
 * on which rows happened to enable them — three unrestricted rooms disabled the
 * lookup, the one restricted room enabled it, and the cell settled showing raw
 * ids.
 */
function AccessChips({
  room,
  companyNames,
  personNames,
}: {
  room: Room;
  companyNames: Map<string, string>;
  personNames: Map<string, string>;
}): ReactElement {
  const { t } = useTranslation();

  if (room.allowed_company_ids.length === 0 && room.allowed_employee_ids.length === 0) {
    return <span className="chip">{t('rm_open_all')}</span>;
  }

  const names = [
    ...room.allowed_company_ids.map((id) => companyNames.get(id) ?? id),
    ...room.allowed_employee_ids.map((id) => personNames.get(id) ?? id),
  ];

  return (
    <>
      {names.map((name, index) => (
        <span className="chip" key={`${name}-${index}`}>
          {name}
        </span>
      ))}
    </>
  );
}

function RoomDialog({
  room,
  onClose,
  onSaved,
}: {
  room: Room | null;
  onClose: () => void;
  onSaved: (created: boolean) => void;
}): ReactElement {
  const { t } = useTranslation();
  const { notify } = useToast();

  const [name, setName] = useState(room?.name ?? '');
  const [capacity, setCapacity] = useState(String(room?.capacity ?? 8));
  const [status, setStatus] = useState<RoomStatus>(room?.status ?? 'available');
  const [companyIds, setCompanyIds] = useState<string[]>(room?.allowed_company_ids ?? []);
  const [employeeIds, setEmployeeIds] = useState<string[]>(room?.allowed_employee_ids ?? []);
  const [resourceGoogle, setResourceGoogle] = useState(room?.resource_email_google ?? '');
  const [resourceMicrosoft, setResourceMicrosoft] = useState(room?.resource_email_microsoft ?? '');
  const [inboundEnabled, setInboundEnabled] = useState(room?.inbound_enabled ?? false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const companies = useQuery({
    queryKey: ['companies', 'all'],
    queryFn: listAllCompanies,
  });

  const people = useQuery({
    queryKey: ['employees', 'all'],
    queryFn: listAllEmployees,
  });

  const mutation = useMutation({
    mutationFn: () => {
      const body = {
        name,
        capacity: Number(capacity),
        status,
        allowed_company_ids: companyIds,
        allowed_employee_ids: employeeIds,
        /*
         * Both addresses, each sent as the form holds it. A room is one physical
         * place with an entry in each platform's directory, and filling in only
         * one leaves the other platform unable to see it is busy.
         */
        resource_email_google: resourceGoogle.trim() || null,
        resource_email_microsoft: resourceMicrosoft.trim() || null,
        inbound_enabled: inboundEnabled,
      };
      return room ? updateRoom(room.id, body) : createRoom(body);
    },
    onSuccess: () => onSaved(room === null),
    onError: (error) => {
      if (error instanceof ApiClientError) {
        // Every offending field at once (§8.6.3).
        setFieldErrors(error.fieldErrors);
        notify(error.message, 'bad');
      }
    },
  });

  function submit(event: FormEvent): void {
    event.preventDefault();
    setFieldErrors({});
    mutation.mutate();
  }

  // Resigned people cannot be allow-listed, so they are never offered.
  const selectable = (people.data ?? []).filter((p) => p.status !== 'resigned');

  return (
    <Modal
      wide
      title={room ? t('rm_edit') : t('rm_new')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button
            type="submit"
            form="room-form"
            className="btn primary"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? t('saving') : t('save')}
          </button>
        </>
      }
    >
      <form id="room-form" onSubmit={submit} noValidate>
        <div className="field">
          <label htmlFor="room-name">{t('rm_name')}</label>
          <input
            id="room-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={fieldErrors.name ? 'bad' : ''}
            aria-invalid={Boolean(fieldErrors.name)}
            aria-describedby={fieldErrors.name ? 'room-name-error' : undefined}
          />
          {fieldErrors.name ? (
            <div id="room-name-error" className="field-error" role="alert">
              {fieldErrors.name}
            </div>
          ) : null}
        </div>

        <div className="grid2">
          <div className="field">
            <label htmlFor="room-capacity">{t('rm_cap')}</label>
            <input
              id="room-capacity"
              type="number"
              min={1}
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              className={fieldErrors.capacity ? 'bad' : ''}
              aria-invalid={Boolean(fieldErrors.capacity)}
              aria-describedby={fieldErrors.capacity ? 'room-capacity-error' : undefined}
            />
            {fieldErrors.capacity ? (
              <div id="room-capacity-error" className="field-error" role="alert">
                {fieldErrors.capacity}
              </div>
            ) : null}
          </div>

          <div className="field">
            <label htmlFor="room-status">{t('rm_status')}</label>
            <select
              id="room-status"
              value={status}
              onChange={(e) => setStatus(e.target.value as RoomStatus)}
            >
              {STATUSES.map((value) => (
                <option key={value} value={value}>
                  {t(`rs_${value}`)}
                </option>
              ))}
            </select>
            <div className="field-note">{t('rm_status_hint')}</div>
          </div>
        </div>

        {/*
          FEAT-018 Stage 4 — the room's own calendar.

          Optional, and most rooms will have nothing here for a while: creating
          the resources is administrator work in Google Workspace or Microsoft
          365. A room without one keeps working exactly as before — the address
          is simply left out of the mirrored event.
        */}
        <div className="grid2">
          <div className="field">
            <label htmlFor="room-resource-google">{t('rm_resource_google')}</label>
            <input
              id="room-resource-google"
              type="email"
              autoComplete="off"
              spellCheck={false}
              placeholder="boud.ai_majlis@resource.calendar.google.com"
              value={resourceGoogle}
              onChange={(e) => setResourceGoogle(e.target.value)}
              className={fieldErrors.resource_email_google ? 'bad' : ''}
              aria-invalid={Boolean(fieldErrors.resource_email_google)}
              aria-describedby={
                fieldErrors.resource_email_google
                  ? 'room-resource-google-error'
                  : 'room-resource-hint'
              }
            />
            {fieldErrors.resource_email_google ? (
              <div id="room-resource-google-error" className="field-error" role="alert">
                {fieldErrors.resource_email_google}
              </div>
            ) : (
              <div id="room-resource-hint" className="field-note">
                {t('rm_resource_hint')}
              </div>
            )}
          </div>

          {/*
            A second address, not an alternative to the first.
            
            A room is one physical place with an entry in each platform's
            directory, and neither platform will accept the other's address:
            invited from Outlook, a Google resource is an attendee that never
            answers, so the room reads free in Outlook while Boud holds it.
            Filling in both is what makes a shared building work.
            
            The platform picker is gone with them. The field an address is typed
            into *is* the platform, which removes the whole class of "address
            entered, platform not chosen" mistakes.
          */}
          <div className="field">
            <label htmlFor="room-resource-microsoft">{t('rm_resource_microsoft')}</label>
            <input
              id="room-resource-microsoft"
              type="email"
              autoComplete="off"
              spellCheck={false}
              placeholder="majlis@boud.ai"
              value={resourceMicrosoft}
              onChange={(e) => setResourceMicrosoft(e.target.value)}
              className={fieldErrors.resource_email_microsoft ? 'bad' : ''}
              aria-invalid={Boolean(fieldErrors.resource_email_microsoft)}
              aria-describedby={
                fieldErrors.resource_email_microsoft ? 'room-resource-microsoft-error' : undefined
              }
            />
            {fieldErrors.resource_email_microsoft ? (
              <div id="room-resource-microsoft-error" className="field-error" role="alert">
                {fieldErrors.resource_email_microsoft}
              </div>
            ) : null}
          </div>
        </div>

        {/*
          FEAT-018 Stage 7 — offered only once the room has a calendar to read.
          The server refuses the combination anyway; hiding it here means nobody
          is invited to turn on a switch that cannot do anything.
        */}
        {resourceGoogle.trim() || resourceMicrosoft.trim() ? (
          <div className="field">
            <label className="check" htmlFor="room-inbound">
              <input
                id="room-inbound"
                type="checkbox"
                checked={inboundEnabled}
                onChange={(e) => setInboundEnabled(e.target.checked)}
                aria-describedby="room-inbound-hint"
              />
              <span>{t('rm_inbound')}</span>
            </label>
            {fieldErrors.inbound_enabled ? (
              <div className="field-error" role="alert">
                {fieldErrors.inbound_enabled}
              </div>
            ) : (
              <div id="room-inbound-hint" className="field-note">
                {t('rm_inbound_hint')}
              </div>
            )}
          </div>
        ) : null}

        <div className="field">
          <div className="lbl">{t('rm_access')}</div>
          <div className="field-note">{t('rm_access_hint')}</div>
        </div>

        <div className="field">
          <div className="lbl">{t('rm_companies')}</div>
          {/*
            Toggle buttons rather than a multi-select, as the prototype has it:
            the list is short and every option is visible without opening
            anything.
          */}
          <div className="chips">
            {companies.data?.map((company) => {
              const on = companyIds.includes(company.id);
              return (
                <button
                  key={company.id}
                  type="button"
                  className={on ? 'tab on' : 'tab'}
                  aria-pressed={on}
                  onClick={() =>
                    setCompanyIds((ids) =>
                      on ? ids.filter((id) => id !== company.id) : [...ids, company.id],
                    )
                  }
                >
                  {company.name}
                </button>
              );
            })}
          </div>
        </div>

        <PeoplePicker
          id="room-people"
          label={t('rm_people')}
          placeholder={t('rm_search_person')}
          people={selectable}
          selectedIds={employeeIds}
          onChange={setEmployeeIds}
          {...(fieldErrors.allowed_employee_ids ? { error: fieldErrors.allowed_employee_ids } : {})}
        />
      </form>
    </Modal>
  );
}

/**
 * REQ-045 — the confirmation says how many reservations the delete would cancel.
 *
 * The count is fetched rather than guessed: "delete this room" and "delete this
 * room and cancel 6 meetings other people are expecting" are different
 * decisions.
 */
function DeleteRoomDialog({
  room,
  onClose,
  onDeleted,
}: {
  room: Room;
  onClose: () => void;
  onDeleted: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { notify } = useToast();

  const impact = useQuery({
    queryKey: ['room-impact', room.id],
    queryFn: () => roomImpact(room.id),
  });

  const mutation = useMutation({
    mutationFn: () => deleteRoom(room.id),
    onSuccess: onDeleted,
    onError: (error) => {
      if (error instanceof ApiClientError) notify(error.message, 'bad');
    },
  });

  return (
    <Modal
      title={t('rm_del')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button
            type="button"
            className="btn danger"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? t('saving') : t('confirm_del')}
          </button>
        </>
      }
    >
      <p>{t('rm_del_q')}</p>

      {impact.isPending ? (
        <div className="empty" role="status" aria-live="polite">
          {t('loading')}
        </div>
      ) : impact.data && impact.data.upcoming_bookings > 0 ? (
        <div className="login-error" role="alert">
          <Icon name="info" />
          <span>
            <b>{impact.data.upcoming_bookings}</b> {t('rm_del_n')}
          </span>
        </div>
      ) : null}
    </Modal>
  );
}
